import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnimatedSection({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className={className}>
      {children}
    </motion.div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const settings = useSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("تم إرسال رسالتك بنجاح! سنتواصل معك قريبًا");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("حدث خطأ. يرجى المحاولة لاحقًا");
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#111112]">
          <div className="absolute top-1/3 left-1/3 w-80 h-80 bg-primary/8 rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">تواصل معنا</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              نحن هنا <span className="text-primary">لمساعدتك</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl">
              لا تتردد في التواصل معنا. فريقنا جاهز للإجابة على استفساراتك ومساعدتك في تحقيق أهدافك.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <div className="space-y-6">
                  <div className="bg-[#161618] border border-white/5 rounded-xl p-6" data-testid="contact-info-phone">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold font-['Zain'] mb-1">اتصل بنا</h3>
                        <p className="text-zinc-400 text-sm" dir="ltr">{settings.phone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#161618] border border-white/5 rounded-xl p-6" data-testid="contact-info-email">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold font-['Zain'] mb-1">البريد الإلكتروني</h3>
                        <p className="text-zinc-400 text-sm">{settings.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#161618] border border-white/5 rounded-xl p-6" data-testid="contact-info-location">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold font-['Zain'] mb-1">الموقع</h3>
                        <p className="text-zinc-400 text-sm">{settings.address}</p>
                      </div>
                    </div>
                  </div>

                  <a
                    href={`https://wa.me/${settings.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-[#25D366]/10 border border-[#25D366]/20 rounded-xl p-5 hover:bg-[#25D366]/15 transition-colors"
                    data-testid="contact-whatsapp"
                  >
                    <MessageCircle size={24} className="text-[#25D366]" />
                    <div>
                      <h3 className="text-white font-semibold font-['Zain'] text-sm">تواصل عبر واتساب</h3>
                      <p className="text-zinc-400 text-xs">رد فوري وسريع</p>
                    </div>
                  </a>
                </div>
              </AnimatedSection>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <AnimatedSection>
                <form onSubmit={handleSubmit} className="bg-[#161618] border border-white/5 rounded-xl p-6 md:p-8 space-y-5" data-testid="contact-form">
                  <h2 className="text-xl font-bold font-['Zain'] text-white mb-2">أرسل لنا رسالة</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-400 text-sm mb-1.5 block">الاسم *</label>
                      <Input
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        className="bg-background border-white/10 text-white text-right"
                        placeholder="اسمك الكامل"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm mb-1.5 block">البريد الإلكتروني *</label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                        className="bg-background border-white/10 text-white text-right"
                        placeholder="email@example.com"
                        dir="ltr"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-400 text-sm mb-1.5 block">الهاتف</label>
                      <Input
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        className="bg-background border-white/10 text-white text-right"
                        placeholder="+966 5X XXX XXXX"
                        dir="ltr"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm mb-1.5 block">الموضوع *</label>
                      <Input
                        value={form.subject}
                        onChange={e => setForm({ ...form, subject: e.target.value })}
                        className="bg-background border-white/10 text-white text-right"
                        placeholder="موضوع الرسالة"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1.5 block">الرسالة *</label>
                    <Textarea
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      className="bg-background border-white/10 text-white text-right min-h-[120px]"
                      placeholder="اكتب رسالتك هنا..."
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-primary hover:bg-[#E54D2E] text-white px-8 red-glow w-full md:w-auto"
                    data-testid="contact-submit"
                  >
                    {loading ? "جاري الإرسال..." : "إرسال الرسالة"}
                    <Send size={16} className="ms-2" />
                  </Button>
                </form>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
